import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from 'shared/hooks/useAuth';
import { sessionHasPermission } from 'shared/utils/sessionPermissions';
import {
  emptyDeploymentStatusDates,
  emptyDeploymentStatusApprovals,
  mapDeploymentStatusApprovalsFromApi,
} from '../../../shared/DeploymentLifecycleFields';
import DeploymentStatusTable from '../../../shared/DeploymentStatusTable';
import DeploymentStageApprovalDialog from '../../../shared/DeploymentStageApprovalDialog';
import { softwareApi } from '../../../api/systemRegisterApi';
import { apiErrorMessage } from '../../../utils/responseHelpers';

const FIRST_SYSTEM_APPROVAL_PERMISSION = 'PERFORM_FIRST_SYSTEM_APPROVAL';
const SECOND_SYSTEM_APPROVAL_PERMISSION = 'PERFORM_SECOND_SYSTEM_APPROVAL';

function isAdminRole(user) {
  const role = user?.data?.role;
  const normalized = String(role ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
  return normalized === 'ADMIN';
}

/**
 * Approval & Sign-off — deployment status table with 1st / 2nd Approval actions.
 * Dialog: Approve or Decline (comment required to decline).
 */
const WizardStepApprovalSignOff = ({
  softwareId,
  identificationValues,
  onIdentificationSetField,
}) => {
  const { user } = useAuth();
  const profile = user?.data;
  const isAdmin = isAdminRole(user);
  const canFirstApproval =
    isAdmin || sessionHasPermission(profile, FIRST_SYSTEM_APPROVAL_PERMISSION);
  const canSecondApproval =
    isAdmin || sessionHasPermission(profile, SECOND_SYSTEM_APPROVAL_PERMISSION);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContext, setDialogContext] = useState(null);

  const currentUser = useMemo(
    () => ({
      name: user?.data?.name?.trim() || '',
      email: user?.data?.email?.trim() || '',
    }),
    [user]
  );

  const idValues = identificationValues || {};

  const nowDatetimeLocal = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const applyDetailToState = useCallback(
    (detail) => {
      if (!detail) return;
      if (detail.deploymentStatuses) {
        onIdentificationSetField?.(
          'deploymentStatusApprovals',
          mapDeploymentStatusApprovalsFromApi(detail.deploymentStatuses)
        );
        onIdentificationSetField?.('deploymentStatusesList', detail.deploymentStatuses);
      }
      if (detail.deploymentStatus) {
        onIdentificationSetField?.('deploymentStatus', detail.deploymentStatus);
      }
    },
    [onIdentificationSetField]
  );

  const handleApprovalClick = useCallback(
    ({ status, slot, stageDate, existingComment }) => {
      const allowed = slot === 1 ? canFirstApproval : canSecondApproval;
      if (!allowed) {
        toast.error('You do not have permission for this approval.');
        return;
      }
      if (!softwareId) {
        toast.error('Save Software Identification first so approvals can be stored.');
        return;
      }
      const name = currentUser.name;
      const email = currentUser.email;
      if (!name && !email) {
        toast.error('Logged-in user name or email is required to approve or decline.');
        return;
      }
      setDialogContext({
        status,
        slot,
        name: name || email,
        email: email || '',
        stageDate: stageDate || undefined,
        existingComment: existingComment || '',
      });
      setDialogOpen(true);
    },
    [softwareId, currentUser, canFirstApproval, canSecondApproval]
  );

  const handleDialogSubmit = useCallback(
    async ({ decision, comment }) => {
      if (!dialogContext || !softwareId) return;

      setSaving(true);
      try {
        const approvedAt = nowDatetimeLocal();
        const approvedAtApi =
          approvedAt && String(approvedAt).length === 16 ? `${approvedAt}:00` : approvedAt;

        const response = await softwareApi.recordDeploymentStageApproval(softwareId, {
          status: dialogContext.status,
          slot: dialogContext.slot,
          name: dialogContext.name || dialogContext.email,
          email: dialogContext.email || '',
          approvedAt: approvedAtApi,
          decision,
          comment: comment || undefined,
          ...(dialogContext.stageDate ? { stageDate: dialogContext.stageDate } : {}),
        });

        const detail = response?.data?.data ?? response?.data ?? null;
        applyDetailToState(detail);

        const slotLabel = dialogContext.slot === 1 ? '1st' : '2nd';
        const stageLabel = String(dialogContext.status || '').replaceAll('_', ' ').toLowerCase();
        toast.success(
          decision === 'DECLINED'
            ? `${slotLabel} approval declined for ${stageLabel}. Stage is DECLINED until the comment is addressed.`
            : `${slotLabel} approval recorded for ${stageLabel}.`
        );
        setDialogOpen(false);
        setDialogContext(null);
      } catch (error) {
        toast.error(apiErrorMessage(error, 'Failed to save approval decision.'));
      } finally {
        setSaving(false);
      }
    },
    [dialogContext, softwareId, applyDetailToState]
  );

  return (
    <>
      <DeploymentStatusTable
        deploymentStatus={idValues.deploymentStatus}
        deploymentStatusDates={idValues.deploymentStatusDates || emptyDeploymentStatusDates()}
        deploymentStatusApprovals={
          idValues.deploymentStatusApprovals || emptyDeploymentStatusApprovals()
        }
        deploymentStatusesFromApi={idValues.deploymentStatusesList || []}
        showAllStages
        approvalActions
        onApprovalClick={handleApprovalClick}
        actionsDisabled={saving}
        canFirstApproval={canFirstApproval}
        canSecondApproval={canSecondApproval}
      />

      <DeploymentStageApprovalDialog
        open={dialogOpen}
        onClose={() => {
          if (!saving) {
            setDialogOpen(false);
            setDialogContext(null);
          }
        }}
        onSubmit={handleDialogSubmit}
        stageStatus={dialogContext?.status}
        slot={dialogContext?.slot}
        currentUser={currentUser}
        saving={saving}
        existingComment={dialogContext?.existingComment || ''}
      />
    </>
  );
};

export default WizardStepApprovalSignOff;
